// ============================================================
//  DASHBOARD PAGE — ADMIN DASHBOARD
//  Path: src/pages/DashboardPage.tsx
//  Access: super_admin, admin
// ============================================================

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getDashboardStats }          from '../services/admin.service'
import { useAuth }                    from '../context/AuthContext'
import TripBars                       from '../components/TripBars'

interface Stats {
  totalUsers: number; totalCustomers: number; totalExperts: number
  pendingExperts: number; activeUsers: number
  pendingBackgroundChecks: number; totalRevenue: number; totalPlatformFees: number
  totalPlatformRevenue?: number; totalTaxCollected?: number; totalPlatformKeeps?: number; totalStripeFees?: number
  // Held on customers' cards, not charged yet — shown only when the backend sends it.
  totalOnHold?: number
  // New — online/active counts for the live platform health view
  activeCustomers?: number   // customers who have posted at least one task
  activeExperts?: number     // approved experts
  onlineExperts?: number     // experts with isOnline = true right now
  // Tasks waiting for an admin decision — completion disputes and
  // no-show reports sent to review.
  openDisputes?: number
  // Approved taskers who have not finished payout / GST-HST after stalledSetupDays.
  stalledSetup?: number
  stalledSetupDays?: number
}

const DashboardPage: React.FC = () => {
  const { user }              = useAuth()
  const [stats, setStats]     = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  // FIX (production-readiness pass): this catch block used to be empty
  // — a failed stats fetch (token expired, API down, network drop) left
  // `stats` null and every card silently rendered 0/— via its `?? 0`
  // fallback. That reads as "the platform genuinely has zero users,"
  // which is exactly the wrong signal during a real incident. Now a
  // failure shows a banner instead of masquerading as good news, and
  // distinguishes a network/timeout failure (api.ts tags these with
  // isNetworkError) from a real server error.
  const [error, setError]     = useState('')
  const [retrying, setRetrying] = useState(false)

  const fetchStats = async () => {
    try {
      const res = await getDashboardStats()
      setStats(res.data.data)
      setError('')
    } catch (err: any) {
      console.error('Failed to fetch dashboard stats:', (err as any)?.response?.status, (err as any)?.message)
      setError(err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Failed to load dashboard stats.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchStats() }, [])

  const retry = async () => {
    setRetrying(true)
    await fetchStats()
    setRetrying(false)
  }

  // FIX: with stats unavailable the cards still rendered 0 / $0.00 via
  // `?? 0` — the banner said "failed" but the numbers underneath said
  // "zero users, zero revenue", and the banner could be dismissed,
  // leaving only the zeros. Every number now shows "—" when there is no
  // data, and the banner offers a retry instead of a dismiss.
  const num = (v: number | undefined | null) => (v != null ? v : '—')
  const money = (v: number | undefined | null) => (v != null ? `$${v.toFixed(2)}` : '—')

  const getGreeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 18) return 'Good afternoon'
    return 'Good evening'
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading...</div></div>
  }

  const isSuperAdmin = user?.roles.includes('super_admin')

  const cards = [
    { label: 'Total Users',       value: num(stats?.totalUsers),      icon: '👥', color: 'bg-brand-600/20 text-brand-400' },
    { label: 'Customers',         value: num(stats?.totalCustomers),  icon: '🛒', color: 'bg-green-600/20 text-green-400' },
    { label: 'Experts',           value: num(stats?.totalExperts),    icon: '🔧', color: 'bg-purple-600/20 text-purple-400' },
    { label: 'Pending Experts',   value: num(stats?.pendingExperts),  icon: '⏳', color: 'bg-yellow-600/20 text-yellow-400' },
    { label: 'Active Users',      value: num(stats?.activeUsers),     icon: '✅', color: 'bg-teal-600/20 text-teal-400' },
  ]

  // Live platform health — online/active right now
  const liveCards = [
    {
      label: 'Experts Online Now',
      value: stats?.onlineExperts != null ? stats.onlineExperts : '—',
      icon: '🟢',
      color: 'bg-green-600/20 text-green-400',
      hint: 'Taskers with isOnline = true',
    },
    {
      label: 'Active Experts',
      value: stats?.activeExperts != null ? stats.activeExperts : stats?.totalExperts ?? '—',
      icon: '🔧',
      color: 'bg-purple-600/20 text-purple-400',
      hint: 'Approved experts on the platform',
    },
    {
      label: 'Active Customers',
      value: stats?.activeCustomers != null ? stats.activeCustomers : stats?.totalCustomers ?? '—',
      icon: '🛒',
      color: 'bg-blue-600/20 text-blue-400',
      hint: 'Customers who have posted tasks',
    },
  ]

  const paymentCards = [
    { label: 'Transaction Volume', value: money(stats?.totalRevenue),       icon: '💳', color: 'bg-green-600/20 text-green-400' },
    // Platform Revenue = every application fee MatchnBook received (tax included).
    { label: 'Platform Revenue',   value: money(stats?.totalPlatformRevenue ?? stats?.totalPlatformFees),  icon: '🏦', color: 'bg-brand-600/20 text-brand-400' },
    ...(stats?.totalTaxCollected != null
      ? [
          { label: 'Tax Collected (to remit)', value: money(stats.totalTaxCollected), icon: '🧾', color: 'bg-orange-600/20 text-orange-400' },
          { label: 'MatchnBook Keeps',         value: money(stats.totalPlatformKeeps), icon: '💰', color: 'bg-emerald-600/20 text-emerald-400' },
        ]
      : []),
    ...(stats?.totalOnHold != null
      ? [{ label: 'On Hold (not charged yet)', value: money(stats.totalOnHold), icon: '⏳', color: 'bg-yellow-600/20 text-yellow-400' }]
      : []),
    { label: 'Background Checks Pending', value: num(stats?.pendingBackgroundChecks), icon: '🛡️', color: 'bg-orange-600/20 text-orange-400' },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">{getGreeting()}, {user?.firstName}</h1>
        <p className="text-gray-400 mt-1">Here's what's happening on your platform</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm flex items-center justify-between">
          {error}
          <button onClick={retry} disabled={retrying}
            className="ml-3 px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light disabled:opacity-50 transition-colors shrink-0">
            {retrying ? 'Retrying...' : 'Try again'}
          </button>
        </div>
      )}

      {/* Disputes waiting for a decision — shown prominently only when
          there are some. A customer may be waiting on a refund, and
          nothing else resolves these. */}
      {(stats?.openDisputes ?? 0) > 0 && (
        <Link to="/tasks?status=disputed"
          className="flex items-center justify-between gap-3 bg-red-500/10 border border-red-500/30 rounded-2xl p-5 mb-6 hover:bg-red-500/15 transition-colors">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚖️</span>
            <div>
              <p className="text-white font-semibold">
                {stats!.openDisputes} dispute{stats!.openDisputes === 1 ? '' : 's'} waiting for a decision
              </p>
              <p className="text-xs text-gray-400">Completion disputes and no-show reports sent to review — someone may be waiting on a refund or a payout.</p>
            </div>
          </div>
          <span className="text-sm text-red-400 shrink-0">Review →</span>
        </Link>
      )}

      {/* Approved taskers stuck on "Finish setup" (payout / GST-HST) — they cannot take work until done. */}
      {(stats?.stalledSetup ?? 0) > 0 && (
        <Link to="/experts?setupPending=true"
          className="flex items-center justify-between gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-5 mb-6 hover:bg-yellow-500/15 transition-colors">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏳</span>
            <div>
              <p className="text-white font-semibold">
                {stats!.stalledSetup} approved tasker{stats!.stalledSetup === 1 ? '' : 's'} still not set up after {stats!.stalledSetupDays ?? 3}+ days
              </p>
              <p className="text-xs text-gray-400">They finished approval but not payout (Stripe) and/or GST/HST, so they cannot take work yet. A reminder may help.</p>
            </div>
          </div>
          <span className="text-sm text-yellow-400 shrink-0">See who →</span>
        </Link>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        {cards.map((card) => (
          <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
              <span className="text-lg">{card.icon}</span>
            </div>
            <p className="text-2xl font-bold text-white">{card.value}</p>
            <p className="text-sm text-gray-400 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Live platform health */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Live Platform Health</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {liveCards.map((card) => (
            <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
                <span className="text-lg">{card.icon}</span>
              </div>
              <p className="text-2xl font-bold text-white">{card.value}</p>
              <p className="text-sm text-gray-400 mt-1">{card.label}</p>
              <p className="text-xs text-gray-600 mt-1">{card.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {paymentCards.map((card) => (
          <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.color}`}>
              <span className="text-lg">{card.icon}</span>
            </div>
            <p className="text-2xl font-bold text-white">{card.value}</p>
            <p className="text-sm text-gray-400 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Everyone on the way right now — same bars the customer and tasker see. */}
      <TripBars title="Live trips" />

      <div className="bg-surface-card border border-border-light rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link to="/experts?status=pending" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
            <span className="text-xl">⏳</span>
            <div>
              <p className="text-sm font-medium text-white">Review Experts</p>
              <p className="text-xs text-gray-500">{num(stats?.pendingExperts)} pending</p>
            </div>
          </Link>
          <Link to="/background-checks" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
            <span className="text-xl">🛡️</span>
            <div>
              <p className="text-sm font-medium text-white">Background Checks</p>
              <p className="text-xs text-gray-500">{num(stats?.pendingBackgroundChecks)} awaiting review</p>
            </div>
          </Link>
          <Link to="/tasks?status=disputed" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
            <span className="text-xl">⚖️</span>
            <div>
              <p className="text-sm font-medium text-white">Resolve Disputes</p>
              <p className="text-xs text-gray-500">{num(stats?.openDisputes)} waiting for a decision</p>
            </div>
          </Link>
          <Link to="/tasks" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
            <span className="text-xl">📋</span>
            <div>
              <p className="text-sm font-medium text-white">Monitor Tasks</p>
              <p className="text-xs text-gray-500">Payments, pairs & status</p>
            </div>
          </Link>
          {isSuperAdmin && (
            <Link to="/admins" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
              <span className="text-xl">🔑</span>
              <div>
                <p className="text-sm font-medium text-white">Admin Team</p>
                <p className="text-xs text-gray-500">Manage roles</p>
              </div>
            </Link>
          )}
          <Link to="/version-control" className="flex items-center gap-3 bg-surface-tertiary rounded-xl p-4 hover:bg-border-light transition-colors">
            <span className="text-xl">🔢</span>
            <div>
              <p className="text-sm font-medium text-white">Version Control</p>
              <p className="text-xs text-gray-500">App & policy versions</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage